
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { networkErrorHandler, getErrorMessage, getFallbackData } from '@/utils/networkErrorHandler';

export interface Community {
  id: string;
  name: string;
  description?: string;
  members_count?: number;
  growth_percentage?: number;
  plant_hardiness_zone?: string;
  "Community Type"?: string;
}

export const useCommunities = (options: { limit?: number } = {}) => {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchCommunities = async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
        setError(null);
      }

      const result = await networkErrorHandler.withRetry(async () => {
        let query = supabase
          .from('communities')
          .select('id, name, description, members_count, growth_percentage, plant_hardiness_zone, "Community Type"')
          .order('members_count', { ascending: false });

        if (options.limit) {
          query = query.limit(options.limit);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data;
      }, {
        maxRetries: 2,
        delayMs: 1000
      });

      setCommunities(result || []);
      setRetryCount(0); // Reset retry count on success
      
    } catch (err: any) {
      console.error('Error fetching communities:', err);
      
      const userMessage = getErrorMessage(err);
      setError(userMessage);
      
      // Use fallback data if available
      if (networkErrorHandler.isOffline()) {
        const fallbackData = getFallbackData('communities');
        setCommunities(fallbackData);
      } else {
        setCommunities([]);
      }
      
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const retry = () => {
    fetchCommunities(true);
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  return { 
    communities, 
    loading, 
    error, 
    retryCount,
    refetch: fetchCommunities, 
    retry,
    isOffline: networkErrorHandler.isOffline()
  };
};
